import { Button, Modal, Result } from "antd";
import { usePrevious } from "common/customHooks";
import { ActionStatus } from "common/enums";
import { LoadingOverlay } from "components";
import ErrorResult from "components/atoms/ErrorResult";
import SuccessResult from "components/atoms/SuccessResult";
import DataSyndicationSubscriptionList from "components/molecules/DataSyndicationSubscriptionList";
import ManageDataSyndicationSubscription from "components/organisms/ManageDataSyndicationSubscription";
import AppPaths from "constants/appPaths";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import actions from "store/actions";
import IAction from "types/IAction";

interface RouteParams {
    tenant: string;
    configurationItem: string;
}

interface IDataSyndicationSubscriptionList {
    Items: IDataSyndicationSubscription[];
}

interface IResult {
    success: boolean;
    title: string;
    message: string;
    onOkClicked: () => void;
}

interface IDataSyndicationSubscriptionProps extends RouteComponentProps<RouteParams> {
    action?: string;
    subscriptionId?: string;
    selectedSubscription?: IDataSyndicationSubscription;
    getDataSyndicationSubscriptionList: () => void;
    getDataSyndicationSubscription: (subscriptionId: string) => void;
    confirmDataSyndicationSubscription: (confirmSubscriptionParams: IDataSyndicationConfirmSubscription) => void;
    deleteDataSyndicationSubscription: (subscriptionId: string) => void;
    dataSyndicationSubscriptionListResult: IDataSyndicationSubscriptionList;
    getDataSyndicationSubscriptionListAction: IAction;
    getDataSyndicationSubscriptionAction: IAction;
    confirmDataSyndicationSubscriptionActions: Record<string, { status: string; error?: IActionError }>;
    deleteDataSyndicationSubscriptionActions: Record<string, { status: string; error?: IActionError }>;
}

const DataSyndicationSubscription: React.FC<IDataSyndicationSubscriptionProps> = ({
    action,
    match,
    history,
    subscriptionId,
    selectedSubscription,
    getDataSyndicationSubscriptionList,
    getDataSyndicationSubscription,
    confirmDataSyndicationSubscription,
    deleteDataSyndicationSubscription,
    dataSyndicationSubscriptionListResult,
    getDataSyndicationSubscriptionAction,
    getDataSyndicationSubscriptionListAction,
    confirmDataSyndicationSubscriptionActions,
    deleteDataSyndicationSubscriptionActions,
}) => {
    const [loadingMessage, setLoadingMessage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [mountingComponent, setMountingComponent] = useState(true);
    const [result, setResult] = useState<IResult | null>(null);
    const previousConfirmDataSyndicationSubscriptionActions = usePrevious(confirmDataSyndicationSubscriptionActions);
    const previousDeleteDataSyndicationSubscriptionActions = usePrevious(deleteDataSyndicationSubscriptionActions);

    const goBackToSubscriptionList = useCallback(() => {
        history.push(AppPaths.TENANT_CONFIGURATION_ITEM.replace(":tenant", match.params.tenant).replace(":configurationItem", match.params.configurationItem.toLowerCase()));
    }, [history, match.params]);

    useEffect(() => {
        if (mountingComponent) {
            setMountingComponent(false);
            if (!action) {
                setLoadingMessage("Loading subscriptions...");
                getDataSyndicationSubscriptionList();
            } else {
                if (subscriptionId) {
                    setLoadingMessage("Loading subscription details...");
                    getDataSyndicationSubscription(subscriptionId);
                }
            }
        }
    }, [mountingComponent, action, subscriptionId, getDataSyndicationSubscriptionList, getDataSyndicationSubscription]);

    useEffect(() => {
        if (
            confirmDataSyndicationSubscriptionActions &&
            previousConfirmDataSyndicationSubscriptionActions &&
            confirmDataSyndicationSubscriptionActions !== previousConfirmDataSyndicationSubscriptionActions
        ) {
            const subscriptionAction = selectedSubscription && confirmDataSyndicationSubscriptionActions[selectedSubscription.subscriptionId];

            if (subscriptionAction) {
                if (subscriptionAction.status === ActionStatus.SUCCESS) {
                    setResult({
                        success: true,
                        title: "Confirm Subscription",
                        message: "Subscription confirmed successfully.",
                        onOkClicked: () => {
                            setResult(null);
                            goBackToSubscriptionList();
                        },
                    });
                } else if (subscriptionAction.status === ActionStatus.FAILED) {
                    setResult({
                        success: false,
                        title: "Confirm Subscription",
                        message: subscriptionAction.error?.message || "Failed to confirm subscription.",
                        onOkClicked: () => setResult(null),
                    });
                }
            }
        }
    }, [confirmDataSyndicationSubscriptionActions, previousConfirmDataSyndicationSubscriptionActions, selectedSubscription, goBackToSubscriptionList]);

    useEffect(() => {
        if (
            deleteDataSyndicationSubscriptionActions &&
            previousDeleteDataSyndicationSubscriptionActions &&
            deleteDataSyndicationSubscriptionActions !== previousDeleteDataSyndicationSubscriptionActions
        ) {
            // Check for any subscription that has a delete action
            const deletedSubscriptionId = Object.keys(deleteDataSyndicationSubscriptionActions).find((id) => {
                const currentAction = deleteDataSyndicationSubscriptionActions[id];
                const previousAction = previousDeleteDataSyndicationSubscriptionActions[id];
                return currentAction && currentAction !== previousAction;
            });

            if (deletedSubscriptionId) {
                setLoadingMessage("");
                setLoading(false);
                const deleteAction = deleteDataSyndicationSubscriptionActions[deletedSubscriptionId];
                if (deleteAction.status === ActionStatus.SUCCESS) {
                    setResult({
                        success: true,
                        title: "Delete Subscription",
                        message: "Subscription deleted successfully.",
                        onOkClicked: () => {
                            setResult(null);
                            getDataSyndicationSubscriptionList();
                        },
                    });
                } else if (deleteAction.status === ActionStatus.FAILED) {
                    setResult({
                        success: false,
                        title: "Delete Subscription",
                        message: deleteAction.error?.message || "Failed to delete subscription.",
                        onOkClicked: () => setResult(null),
                    });
                }
            }
        }
    }, [deleteDataSyndicationSubscriptionActions, previousDeleteDataSyndicationSubscriptionActions, getDataSyndicationSubscriptionList]);

    const showViewSubscription = useCallback(
        (subscriptionId: string) => {
            history.push(
                AppPaths.TENANT_CONFIGURATION_ITEM_ID_ACTION.replace(":tenant", match.params.tenant)
                    .replace(":configurationItem", match.params.configurationItem.toLowerCase())
                    .replace(":itemId", subscriptionId)
                    .replace(":itemAction", "view"),
            );
        },
        [history, match.params],
    );

    const showEditSubscription = useCallback(
        (subscriptionId: string) => {
            history.push(
                AppPaths.TENANT_CONFIGURATION_ITEM_ID_ACTION.replace(":tenant", match.params.tenant)
                    .replace(":configurationItem", match.params.configurationItem.toLowerCase())
                    .replace(":itemId", subscriptionId)
                    .replace(":itemAction", "edit"),
            );
        },
        [history, match.params],
    );

    const handleConfirmSubscription = useCallback(
        (confirmSubscriptionParams: IDataSyndicationConfirmSubscription) => {
            Modal.confirm({
                title: "Confirm Subscription",
                content: "Are you sure you want to confirm this subscription?",
                okText: "Yes",
                cancelText: "No",
                centered: true,
                onOk: () => {
                    confirmDataSyndicationSubscription(confirmSubscriptionParams);
                },
            });
        },
        [confirmDataSyndicationSubscription],
    );

    const handleDeleteSubscription = useCallback(
        (subscriptionId: string) => {
            Modal.confirm({
                title: "Delete Subscription",
                content: "Are you sure you want to delete this subscription?",
                okText: "Yes",
                cancelText: "No",
                centered: true,
                onOk: () => {
                    setLoading(true);
                    setLoadingMessage("Deleting subscription...");
                    deleteDataSyndicationSubscription(subscriptionId);
                },
            });
        },
        [deleteDataSyndicationSubscription],
    );

    const handleConfirmSubscriptionDetails = useCallback(
        (confirmSubscriptionParams: IDataSyndicationConfirmSubscription) => {
            handleConfirmSubscription(confirmSubscriptionParams);
        },
        [handleConfirmSubscription],
    );

    const onCancelConfirmSubscription = useCallback(() => {
        goBackToSubscriptionList();
    }, [goBackToSubscriptionList]);

    const isBusy = useCallback(() => {
        return loading || getDataSyndicationSubscriptionListAction.status === ActionStatus.PENDING || getDataSyndicationSubscriptionAction.status === ActionStatus.PENDING;
    }, [loading, getDataSyndicationSubscriptionListAction.status, getDataSyndicationSubscriptionAction.status]);

    const getComponentContent = useCallback(() => {
        if (!action) {
            if (getDataSyndicationSubscriptionListAction.status === ActionStatus.FAILED) {
                return (
                    <Result
                        status="error"
                        title="Failed to load subscriptions"
                        subTitle={getDataSyndicationSubscriptionListAction.error?.message || "An error occurred while loading the subscriptions."}
                        extra={[
                            <Button key="retry" type="primary" onClick={() => getDataSyndicationSubscriptionList()}>
                                Try Again
                            </Button>,
                        ]}
                    />
                );
            }
            return (
                <>
                    {getDataSyndicationSubscriptionListAction.status !== ActionStatus.PENDING ? (
                        <DataSyndicationSubscriptionList
                            items={dataSyndicationSubscriptionListResult.Items}
                            onViewSubscription={showViewSubscription}
                            onEditSubscription={showEditSubscription}
                            onDeleteSubscription={handleDeleteSubscription}
                            permission={{
                                canEdit: true,
                                canView: true,
                                canDelete: true,
                                canAdd: false,
                            }}
                            confirmDataSyndicationSubscriptionActions={confirmDataSyndicationSubscriptionActions}
                        />
                    ) : (
                        <></>
                    )}
                </>
            );
        }

        switch (action.toLowerCase()) {
            case "view":
            case "edit":
                if (getDataSyndicationSubscriptionAction.status === ActionStatus.FAILED) {
                    return (
                        <Result
                            status="error"
                            title="Failed to load subscription"
                            subTitle={getDataSyndicationSubscriptionAction.error?.message || "An error occurred while loading the subscription."}
                            extra={[
                                <Button key="retry" type="primary" onClick={() => subscriptionId && getDataSyndicationSubscription(subscriptionId)}>
                                    Try Again
                                </Button>,
                            ]}
                        />
                    );
                }
                if (getDataSyndicationSubscriptionAction.status === ActionStatus.SUCCESS) {
                    if (selectedSubscription) {
                        const subscriptionAction = confirmDataSyndicationSubscriptionActions[selectedSubscription.subscriptionId];
                        return (
                            <ManageDataSyndicationSubscription
                                subscription={selectedSubscription}
                                onConfirmSubscription={handleConfirmSubscriptionDetails}
                                onCancelClick={onCancelConfirmSubscription}
                                isLoading={subscriptionAction?.status === ActionStatus.PENDING}
                                readOnlyView={action === "view"}
                                permission={{ canAdd: true, canEdit: true }}
                            />
                        );
                    } else {
                        return (
                            <Result
                                status="warning"
                                title="Subscription not found."
                                subTitle="The subscription you are looking for does not exist."
                                extra={
                                    <Button type="primary" onClick={goBackToSubscriptionList}>
                                        Go Back
                                    </Button>
                                }
                            />
                        );
                    }
                }
                return <></>;
            default:
                return <></>;
        }
    }, [
        action,
        selectedSubscription,
        handleConfirmSubscriptionDetails,
        handleDeleteSubscription,
        confirmDataSyndicationSubscriptionActions,
        dataSyndicationSubscriptionListResult.Items,
        showViewSubscription,
        getDataSyndicationSubscriptionAction.status,
        getDataSyndicationSubscriptionAction.error,
        getDataSyndicationSubscriptionListAction.status,
        getDataSyndicationSubscriptionListAction.error,
        getDataSyndicationSubscriptionList,
        getDataSyndicationSubscription,
        subscriptionId,
        goBackToSubscriptionList,
        onCancelConfirmSubscription,
        showEditSubscription,
    ]);

    return (
        <>
            <LoadingOverlay busy={isBusy()} spinner message={loadingMessage} />
            {result && (
                <>
                    <SuccessResult show={result.success} title={result.title} subTitle={result.message} onOkClick={result.onOkClicked} okText="OK" />
                    <ErrorResult show={!result.success} title={result.title} subTitle={result.message} onOkClick={result.onOkClicked} okText="OK" />
                </>
            )}
            {getComponentContent()}
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        getDataSyndicationSubscriptionListAction: state.dataSyndicationSubscription.getDataSyndicationSubscriptionListAction,
        dataSyndicationSubscriptionListResult: state.dataSyndicationSubscription.dataSyndicationSubscriptionListResult,
        selectedSubscription: state.dataSyndicationSubscription.subscription,
        getDataSyndicationSubscriptionAction: state.dataSyndicationSubscription.getDataSyndicationSubscriptionAction,
        confirmDataSyndicationSubscriptionActions: state.dataSyndicationSubscription.confirmDataSyndicationSubscriptionActions,
        deleteDataSyndicationSubscriptionActions: state.dataSyndicationSubscription.deleteDataSyndicationSubscriptionActions,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        getDataSyndicationSubscriptionList: () => dispatch(actions.getDataSyndicationSubscriptionListRequest()),
        getDataSyndicationSubscription: (subscriptionId: string) => dispatch(actions.getDataSyndicationSubscriptionRequest(subscriptionId)),
        confirmDataSyndicationSubscription: (confirmSubscriptionParams: IDataSyndicationConfirmSubscription) =>
            dispatch(actions.confirmDataSyndicationSubscriptionRequest(confirmSubscriptionParams)),
        deleteDataSyndicationSubscription: (subscriptionId: string) => dispatch(actions.deleteDataSyndicationSubscriptionRequest(subscriptionId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DataSyndicationSubscription));
